import React, { useEffect, useMemo, useState } from 'react'
import TextField from '../../components/Forms/TextField'
import SimpleCard from '../../components/templates/SimpleCard'
import styles from './verification.module.scss'
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { IMAGE_FORMATS, PDF_FORMATS } from '../../utils/helpers/fileFormats'
import MainButton from '../../components/templates/MainButton'
import RadioGroup from '../../components/Forms/RadioGroup'
import Radio from '../../components/Forms/Radio'
import FileInput from '../../components/Forms/FileInput'
import SelectField from '../../components/Forms/SelectField';
import useAsync from '../../components/hooks/useAsync'
import useMountedState from '../../components/hooks/useMountedState';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import authService from '../../axios/services/authService';
import { authActions } from '../../redux/actions/authActions';
import LoadingDots from '../../components/Loadings/LoadingDots';
import { TransferFrequencyOpts } from '../../utils/enums/transferFrequencyTypes';
import { TransferMethodOpts } from '../../utils/enums/transferMethod';
import { useToasts } from 'react-toast-notifications'
import bankService from '../../axios/services/bankService';
import Checkbox from '../../components/Forms/Checkbox';
import Button from '../../components/Button';
import DateField from '../../components/Forms/DateField';

const schema = yup.object().shape({
  birthCertificate: yup.mixed()
    .test(
      "required",
      "Akta Pendirian Perusahaan tidak boleh kosong",
      value => value && value.length > 0
    )
    .test(
      "fileFormat",
      "Format file tidak didukung",
      value => value?.[0] ?  PDF_FORMATS.includes(value[0].type) : true
    ),
  sk: yup.mixed()
    .test(
      "required",
      "SK-Kemenkumham tidak boleh kosong",
      value => value && value.length > 0
    )
    .test(
      "fileFormat",
      "Format file tidak didukung",
      value => value?.[0] ?  PDF_FORMATS.includes(value[0].type) : true
    ),
  siupFile: yup.mixed()
    .test(
      "required",
      "NIB Perusahaan tidak boleh kosong",
      value => value && value.length > 0
    )
    .test(
      "fileFormat",
      "Format file tidak didukung",
      value => value?.[0] ?  PDF_FORMATS.includes(value[0].type) : true
    ),
  npwpFile: yup.mixed()
    .test(
      "required",
      "NPWP Perusahaan tidak boleh kosong",
      value => value && value.length > 0
    )
    .test(
      "fileFormat",
      "Format file tidak didukung",
      value => value?.[0] ?  PDF_FORMATS.includes(value[0].type) : true
    ),
  npwp: yup.string().matches(/^[0-9]+$/, "Hanya gunakan angka").required('NPWP tidak boleh kosong').min(15, "NPWP harus lebih dari 15 digit").max(16, "NPWP harus dibawah 16 digit"),
  siup: yup.string().matches(/^[0-9]+$/, "Hanya gunakan angka").required('No.NIB tidak boleh kosong').min(13, "NIB harus 13 digit").max(13, "NIB harus 13 digit"),
  address: yup.string().required('Alamat perusahaan tidak boleh kosong'),
  bankAccountName: yup.string().required('Nama bank tidak boleh kosong'),
  bankAccountNumber: yup.string().required('Nomor rekening tidak boleh kosong'),
  formOfBusinessEntity: yup.string().required('Bentuk badan usaha tidak boleh kosong'),
  businessFields: yup.string().required('Bidang usaha tidak boleh kosong'),
  transferFrequency: yup.string().required('Frekuensi transaksi tidak boleh kosong'),
  // transactionMethod: yup.string().required('Metode transaksi tidak boleh kosong'),
  transactionValue: yup.string().required('Nilai Transaksi Dalam 1 Tahun tidak boleh kosong'),
  // transactionVolume: yup.string().required('Volume Transaksi tidak boleh kosong')
  
  // New fields validation
  actaNumber: yup.string().required('No & Tanggal Akta Pendirian tidak boleh kosong'),
  financialInfoAssets: yup.string().required('Informasi Keuangan (Aset) tidak boleh kosong'),
  financialInfoProfit: yup.string().required('Informasi Keuangan (Laba Usaha) tidak boleh kosong'),
  transactionPurpose: yup.string().required('Tujuan Transaksi tidak boleh kosong'),
  
  // Ownership structure validation
  ownershipStructure: yup.array().of(
    yup.object().shape({
      name: yup.string().required('Nama tidak boleh kosong'),
      nik: yup.string().matches(/^[0-9]+$/, "Hanya gunakan angka").required('NIK tidak boleh kosong').min(16, "NIK harus 16 digit").max(16, "NIK harus 16 digit"),
      ownership: yup.number().required('Persentase kepemilikan tidak boleh kosong').min(0.1, "Minimal 0.1%").max(100, "Maksimal 100%"),
      citizenship: yup.string().required('Kewarganegaraan tidak boleh kosong')
    })
  ).min(1, 'Minimal satu struktur kepemilikan saham'),
  
  // Beneficial owners validation (array)
  beneficialOwners: yup.array().of(
    yup.object().shape({
      name: yup.string().required('Nama Lengkap Pemilik Manfaat tidak boleh kosong'),
      idNumber: yup.string().matches(/^[0-9]+$/, "Hanya gunakan angka").required('NIK Pemilik Manfaat tidak boleh kosong').min(16, "NIK harus 16 digit").max(16, "NIK harus 16 digit"),
      birthPlace: yup.string().required('Tempat Lahir Pemilik Manfaat tidak boleh kosong'),
      birthDate: yup.string().required('Tanggal Lahir Pemilik Manfaat tidak boleh kosong'),
      nationality: yup.string().required('Kewarganegaraan Pemilik Manfaat tidak boleh kosong'),
      address: yup.string().required('Alamat Pemilik Manfaat tidak boleh kosong'),
      phoneNumber: yup.string().required('Nomor Telepon Pemilik Manfaat tidak boleh kosong'),
      email: yup.string().email('Format email tidak valid').required('Email Pemilik Manfaat tidak boleh kosong'),
      relationship: yup.string().required('Hubungan dengan Perusahaan tidak boleh kosong')
    })
  ).min(1, 'Minimal satu data pemilik manfaat')
})

const CompanyForm = () => {
  const { addToast } = useToasts();
  const { push } = useHistory()
  const dispatch = useDispatch()
  const { company } = useSelector(state => state.auth)
  const isMounted = useMountedState()
  const [isLoading, setIsLoading] = useState(false)
  
  // State for dynamic ownership structure
  const [ownershipStructure, setOwnershipStructure] = useState([
    { name: '', nik: '', ownership: '', citizenship: 'WNI' }
  ])
  
  // State for dynamic beneficial owners array
  const [beneficialOwners, setBeneficialOwners] = useState([
    {
      name: '',
      nik: '',
      birthPlace: '',
      birthDate: '',
      ktpAddress: '',
      domicileAddress: '',
      job: '',
      position: '',
      income: '',
      fundSource: {
        operatingProfit: false,
        investmentOutcomes: false,
        depositInterest: false,
        loan: false,
        others: false,
        othersText: ''
      }
    }
  ])
  
  // State for fund source checkboxes
  const [fundSource, setFundSource] = useState({
    operatingProfit: false,
    investmentOutcomes: false,
    depositInterest: false,
    loan: false,
    others: false,
    othersText: ''
  })

  // State for beneficial owner fund source
  const [beneficialOwnerFundSource, setBeneficialOwnerFundSource] = useState({
    operatingProfit: false,
    investmentOutcomes: false,
    depositInterest: false,
    loan: false,
    others: false,
    othersText: ''
  })

  const { register, handleSubmit, errors, watch, formState: { isValid }, setValue, unregister } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      ownershipStructure: ownershipStructure,
      beneficialOwners: beneficialOwners
    }
  });
  
  const {
    birthCertificate,
    sk,
    siupFile,
    npwpFile,
    npwp,
    siup,
    address,
    formOfBusinessEntity,
    businessFields,
    transferFrequency,
    transactionMethod,
    transactionValue,
    transactionVolume,
    bankAccountName,
    bankAccountNumber,
    bankName,
    // New fields
    actaNumber,
    financialInfoAssets,
    financialInfoProfit,
    transactionPurpose,
  } = watch([
    "birthCertificate",
    "sk",
    "siupFile",
    "npwpFile",
    "npwp",
    "siup",
    "address",
    "formOfBusinessEntity",
    "businessFields",
    "transferFrequency",
    "transactionMethod",
    "transactionValue",
    "transactionVolume",
    "bankAccountName",
    "bankAccountNumber",
    "bankName",
    // New fields
    "actaNumber",
    "financialInfoAssets", 
    "financialInfoProfit",
    "transactionPurpose",
  ])

  const onSubmit = (values) => {
    setValue('transactionMethod', "INTERNET_BANKING", { shouldValidate: true })
    const data = {
      ...values,
      birthCertificate: values.birthCertificate[0],
      transactionVolume: "1m",
      sk: values.sk[0],
      siupFile: values.siupFile[0],
      npwpFile: values.npwpFile[0],
      // Add new fields
      fundSource,
      beneficialOwnerFundSource,
      ownershipStructure,
      beneficialOwners
    }

    setIsLoading(true)
    authService.updateCompany(data, company._id)
      .then(({ data }) => {
        return dispatch(authActions.companyLoaded(data))
      })
      .then(() => {
        if (isMounted()) push('/verification')
      })
      .catch((err) => {
        addToast(`${err?.response?.data?.message}`, { appearance: 'danger' });
       })
      .finally(() => { if (isMounted()) setIsLoading(false) })
  }

  const addOwnershipRow = () => {
    const newRow = { name: '', nik: '', ownership: '', citizenship: 'WNI' }
    const newStructure = [...ownershipStructure, newRow]
    setOwnershipStructure(newStructure)
    setValue('ownershipStructure', newStructure, { shouldValidate: true })
  }

  const removeOwnershipRow = (index) => {
    if (ownershipStructure.length > 1) {
      const newStructure = ownershipStructure.filter((_, i) => i !== index)
      setOwnershipStructure(newStructure)
      setValue('ownershipStructure', newStructure, { shouldValidate: true })
    }
  }

  const updateOwnershipRow = (index, field, value) => {
    const newStructure = [...ownershipStructure]
    newStructure[index][field] = value
    setOwnershipStructure(newStructure)
    setValue('ownershipStructure', newStructure, { shouldValidate: true })
  }

  const addBeneficialOwner = () => {
    const newOwner = {
      name: '',
      idNumber: '',
      birthPlace: '',
      birthDate: '',
      nationality: '',
      address: '',
      phoneNumber: '',
      email: '',
      relationship: ''
    }
    const newBeneficialOwners = [...beneficialOwners, newOwner]
    setBeneficialOwners(newBeneficialOwners)
    setValue('beneficialOwners', newBeneficialOwners, { shouldValidate: true })
  }

  const removeBeneficialOwner = (index) => {
    if (beneficialOwners.length > 1) {
      const newBeneficialOwners = beneficialOwners.filter((_, i) => i !== index)
      setBeneficialOwners(newBeneficialOwners)
      setValue('beneficialOwners', newBeneficialOwners, { shouldValidate: true })
    }
  }

  const updateBeneficialOwner = (index, field, value) => {
    const newBeneficialOwners = [...beneficialOwners]
    newBeneficialOwners[index][field] = value
    setBeneficialOwners(newBeneficialOwners)
    setValue('beneficialOwners', newBeneficialOwners, { shouldValidate: true })
  }

  const handleFundSourceChange = (field, checked) => {
    setFundSource(prev => ({
      ...prev,
      [field]: checked
    }))
  }

  const handleBeneficialOwnerFundSourceChange = (field, checked) => {
    setBeneficialOwnerFundSource(prev => ({
      ...prev,
      [field]: checked
    }))
  }

  const {
    pending: isGettingBusinessField,
    value: { data: { bussinessesField = [] } = {} } = {}
  } = useAsync(authService.businessField, true)
  const businessFieldOpts = useMemo(() => bussinessesField.map(el => ({ label: el.description, value: el.id })), [bussinessesField])

  const {
    pending: isGettingTransactionValue,
    value: { data: { valueTransactionOneYear = [] } = {} } = {}
  } = useAsync(authService.transactionValue, true)
  const transactionValueOpts = useMemo(() => valueTransactionOneYear.map(el => ({ label: el.description, value: el.id })), [valueTransactionOneYear])

  useEffect(() => {
    register('transactionMethod')
    register('formOfBusinessEntity')
    register('businessFields')
    register('transferFrequency')
    register('transactionValue')
    register('bankAccountNumber')
    register('bankAccountName')
    register('bankName')
    // Register new fields
    register('actaNumber')
    register('financialInfoAssets')
    register('financialInfoProfit')
    register('transactionPurpose')
    register('ownershipStructure')
    register('beneficialOwners')
    return () => {
      unregister('transactionMethod')
      unregister('formOfBusinessEntity')
      unregister('businessFields')
      unregister('transferFrequency')
      unregister('transactionValue')
      unregister('bankAccountNumber')
      unregister('bankAccountName')
      unregister('bankName')
      // Unregister new fields
      unregister('actaNumber')
      unregister('financialInfoAssets')
      unregister('financialInfoProfit')
      unregister('transactionPurpose')
      unregister('ownershipStructure')
      unregister('beneficialOwners')
    }
  }, [register, unregister])
console.log(errors)
  return (
    <SimpleCard>
      {
        isLoading &&
        <LoadingDots className={styles.loading} />
      }
      <p className="font-500 mb-12">Isi Data Perusahaan</p>
      <p className="font-size-12 text-dark-gray">Lanjutkan proses pengisian data dengan mengisi data perusahaan di bawah ini</p>
      <hr />
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.inputWrapper}>
          <div className="row mb-12">
            <div className="col-md-6">
              <TextField
                ref={register}
                name="npwp"
                label="NPWP Perusahaan"
                error={errors.npwp?.message}
                helperText={errors.npwp?.message}
              />
            </div>
            <div className="col-md-6">
              <TextField
                ref={register}
                name="siup"
                label="No.NIB Perusahaan"
                error={errors.siup?.message}
                helperText={errors.siup?.message}
              />
            </div>
          </div>
          <TextField
            ref={register}
            name="address"
            className="mb-24"
            label="Alamat Perusahaan"
            error={errors.address?.message}
            helperText={errors.address?.message}
          />
          <TextField
            ref={register}
            name="phoneNumber"
            className="mb-24"
            label="Nomor HP"
            error={errors.phoneNumber?.message}
            helperText={errors.phoneNumber?.message}
          />
          <SelectField
            value={formOfBusinessEntity}
            onChange={v => setValue('formOfBusinessEntity', v?.value, { shouldValidate: true })}
            options={[
              { label: 'PT', value: 'PT' },
            ]}
            label="Bentuk Badan Usaha"
            className="mb-24"
            error={errors.formOfBusinessEntity?.message}
            helperText={errors.formOfBusinessEntity?.message}
          />
          <SelectField
            isLoading={isGettingBusinessField}
            value={businessFields}
            options={businessFieldOpts}
            onChange={v => setValue('businessFields', v?.value, { shouldValidate: true })}
            label="Bidang Usaha"
            className="mb-24"
            error={errors.businessFields?.message}
            helperText={errors.businessFields?.message}
          />
          <SelectField
            value={transferFrequency}
            onChange={v => setValue('transferFrequency', v?.value, { shouldValidate: true })}
            options={TransferFrequencyOpts}
            label="Estimasi Frekuensi Transaksi"
            className="mb-24"
            error={errors.transferFrequency?.message}
            helperText={errors.transferFrequency?.message}
          />
          {/* <RadioGroup
            row
            label="Metode Transaksi"
            value={transactionMethod}
            onChange={e => }
            className="mb-24 d-none"
            defaultValue="INTERNET_BANKING"
          >
            {
              TransferMethodOpts.map(({ label, value }, i) => (
                <Radio key={i} label={label} value={value} />
              ))
            }
          </RadioGroup> */}
          <SelectField
            isLoading={isGettingTransactionValue}
            value={transactionValue}
            onChange={v => setValue('transactionValue', v?.value, { shouldValidate: true })}
            options={transactionValueOpts}
            label="Nilai Transaksi Dalam 1 Tahun"
            className="mb-24"
            error={errors.transactionValue?.message}
            helperText={errors.transactionValue?.message}
          />
          <p className="font-500 mb-12">Bank Akun</p>
          {/* <p className="font-size-12 text-dark-gray">Lanjutkan proses verifikasi dengan mengisi data perusahaan di bawah ini</p> */}
          <TextField
            ref={register}
            name="bankAccountNumber"
            className="mb-24"
            label="PT Nomor Rekening"
            error={errors.bankAccountNumber?.message}
            helperText={errors.bankAccountNumber?.message}
          />
          <TextField
            ref={register}
            name="bankAccountName"
            className="mb-24"
            label="Nama Rekening"
            error={errors.bankAccountName?.message}
            helperText={errors.bankAccountName?.message}
          />
          {/* <TextField
            ref={register}
            name="bankName"
            className="mb-24"
            label="Nama Bank"
            error={errors.bankName?.message}
            helperText={errors.bankName?.message}
          /> */}
          <SelectField
            value={bankName}
            onChange={v => setValue('bankName', v?.value, { shouldValidate: true })}
            options={[
              { label: "BCA", value: "BCA" },
              { label: "Mandiri", value: "Mandiri" },
              { label: "BRI", value: "BRI" },
              { label: "BNI", value: "BNI" },
              { label: "CIMB", value: "CIMB" },
            ]}
            label="Nama Bank"
            className="mb-24"
            error={errors.bankName?.message}
            helperText={errors.bankName?.message}
          />
          
          {/* New Fields Section */}
          <div className="mb-32">
            <h5 className="font-500 mb-24">Tambahan Data Perusahaan</h5>
            
            {/* No & Tanggal Akta Pendirian */}
            <TextField
              ref={register}
              name="actaNumber"
              className="mb-24"
              label="No & Tanggal Akta Pendirian"
              placeholder="Contoh: AHU-026332.AH.01.30.Tahun 2024"
              error={errors.actaNumber?.message}
              helperText={errors.actaNumber?.message}
            />

            {/* Sumber Dana */}
            <div className="mb-24">
              <p className="font-500 mb-12">Sumber Dana</p>
              <div className="mb-16">
                <Checkbox
                  name="fundSource.operatingProfit"
                  label="Laba Usaha / Operating Profit"
                  checked={fundSource.operatingProfit}
                  onChange={e => handleFundSourceChange('operatingProfit', e.target.checked)}
                  color="primary"
                  className="mb-8"
                />
                <Checkbox
                  name="fundSource.investmentOutcomes"
                  label="Hasil Investasi / Investment outcomes"
                  checked={fundSource.investmentOutcomes}
                  onChange={e => handleFundSourceChange('investmentOutcomes', e.target.checked)}
                  color="primary"
                  className="mb-8"
                />
                <Checkbox
                  name="fundSource.depositInterest"
                  label="Bunga Simpanan / Deposit Interest"
                  checked={fundSource.depositInterest}
                  onChange={e => handleFundSourceChange('depositInterest', e.target.checked)}
                  color="primary"
                  className="mb-8"
                />
                <Checkbox
                  name="fundSource.loan"
                  label="Pinjaman / Loan"
                  checked={fundSource.loan}
                  onChange={e => handleFundSourceChange('loan', e.target.checked)}
                  color="primary"
                  className="mb-8"
                />
                <div className="d-flex align-items-center">
                  <Checkbox
                    name="fundSource.others"
                    label="Lainnya, sebutkan / Others"
                    checked={fundSource.others}
                    onChange={e => handleFundSourceChange('others', e.target.checked)}
                    color="primary"
                    className="mb-8"
                  />
                  {fundSource.others && (
                    <TextField
                      value={fundSource.othersText}
                      onChange={e => setFundSource(prev => ({ ...prev, othersText: e.target.value }))}
                      placeholder="Sebutkan..."
                      className="ml-16"
                      style={{ flex: 1 }}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Informasi Keuangan (Aset) */}
            <RadioGroup
              label="Informasi Keuangan (Aset)"
              value={financialInfoAssets}
              onChange={e => setValue('financialInfoAssets', e.target.value, { shouldValidate: true })}
              className="mb-24"
            >
              <Radio label="<= Rp 100M" value="<= 100M" />
              <Radio label="> Rp 100M - 250M" value="100M-250M" />
              <Radio label="> Rp 250M - 500M" value="250M-500M" />
              <Radio label="> Rp 500M - 1T" value="500M-1T" />
              <Radio label="> Rp 1T" value="> 1T" />
            </RadioGroup>

            {/* Informasi Keuangan (Laba Usaha) */}
            <RadioGroup
              label="Informasi Keuangan (Laba Usaha)"
              value={financialInfoProfit}
              onChange={e => setValue('financialInfoProfit', e.target.value, { shouldValidate: true })}
              className="mb-24"
            >
              <Radio label="<= Rp 100M" value="<= 100M" />
              <Radio label="> Rp 100M - 250M" value="100M-250M" />
              <Radio label="> Rp 250M - 500M" value="250M-500M" />
              <Radio label="> Rp 500M - 1T" value="500M-1T" />
              <Radio label="> Rp 1T" value="> 1T" />
            </RadioGroup>

            {/* Tujuan Transaksi */}
            <RadioGroup
              label="Tujuan Transaksi"
              value={transactionPurpose}
              onChange={e => setValue('transactionPurpose', e.target.value, { shouldValidate: true })}
              className="mb-24"
            >
              <Radio label="Pembelian / Purchase" value="purchase" />
              <Radio label="Pembayaran / Payment" value="payment" />
            </RadioGroup>
          </div>

          {/* Struktur Kepemilikan Saham */}
          <div className="mb-32">
            <div className="d-flex justify-content-between align-items-center mb-16">
              <h5 className="font-500">Struktur Kepemilikan Saham</h5>
              <MainButton 
                type="button" 
                color="primary-light" 
                onClick={addOwnershipRow}
                style={{ padding: '8px 16px', fontSize: '14px' }}
              >
                + Tambah Baris
              </MainButton>
            </div>
            
            {ownershipStructure.map((row, index) => (
              <div key={index} className="border rounded p-16 mb-16" style={{ border: '1px solid #e0e0e0' }}>
                <div className="row mb-12">
                  <div className="col-md-3">
                    <TextField
                      value={row.name}
                      onChange={e => updateOwnershipRow(index, 'name', e.target.value)}
                      label="Nama"
                      placeholder="Nama Lengkap"
                      error={errors.ownershipStructure?.[index]?.name?.message}
                    />
                  </div>
                  <div className="col-md-3">
                    <TextField
                      value={row.nik}
                      onChange={e => updateOwnershipRow(index, 'nik', e.target.value)}
                      label="NIK"
                      placeholder="16 Digit NIK"
                      error={errors.ownershipStructure?.[index]?.nik?.message}
                    />
                  </div>
                  <div className="col-md-2">
                    <TextField
                      value={row.ownership}
                      onChange={e => updateOwnershipRow(index, 'ownership', e.target.value)}
                      label="Kepemilikan (%)"
                      placeholder="Contoh: 50"
                      type="number"
                      error={errors.ownershipStructure?.[index]?.ownership?.message}
                    />
                  </div>
                  <div className="col-md-2">
                    <SelectField
                      value={{ label: row.citizenship === 'WNI' ? 'WNI' : 'WNA', value: row.citizenship }}
                      onChange={v => updateOwnershipRow(index, 'citizenship', v?.value)}
                      options={[
                        { label: 'WNI', value: 'WNI' },
                        { label: 'WNA', value: 'WNA' }
                      ]}
                      label="Kewarganegaraan"
                      error={errors.ownershipStructure?.[index]?.citizenship?.message}
                    />
                  </div>
                  <div className="col-md-2 d-flex align-items-end">
                    {ownershipStructure.length > 1 && (
                      <MainButton
                        type="button"
                        color="danger"
                        onClick={() => removeOwnershipRow(index)}
                        style={{ padding: '8px 12px', fontSize: '12px' }}
                      >
                        Hapus
                      </MainButton>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Data Profil Pemilik Manfaat */}
          <div className="mb-32">
            <div className="d-flex justify-content-between align-items-center mb-24">
              <h5 className="font-500 mb-0">Data Profil Pemilik Manfaat</h5>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={addBeneficialOwner}
                style={{ fontSize: '12px', padding: '6px 12px' }}
              >
                + Tambah Pemilik Manfaat
              </Button>
            </div>

            {beneficialOwners.map((owner, index) => (
              <div key={index} className="mb-24" style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '16px', position: 'relative' }}>
                <div className="d-flex justify-content-between align-items-center mb-16">
                  <h6 className="font-500 mb-0">Pemilik Manfaat {index + 1}</h6>
                  {beneficialOwners.length > 1 && (
                    <Button 
                      variant="text" 
                      color="secondary"
                      onClick={() => removeBeneficialOwner(index)}
                      style={{ fontSize: '12px', padding: '4px 8px', minWidth: 'auto' }}
                    >
                      Hapus
                    </Button>
                  )}
                </div>

                <div className="row mb-12">
                  <div className="col-md-6">
                    <TextField
                      value={owner.name}
                      onChange={e => updateBeneficialOwner(index, 'name', e.target.value)}
                      label="Nama Lengkap"
                      error={errors.beneficialOwners?.[index]?.name?.message}
                      helperText={errors.beneficialOwners?.[index]?.name?.message}
                    />
                  </div>
                  <div className="col-md-6">
                    <TextField
                      value={owner.idNumber}
                      onChange={e => updateBeneficialOwner(index, 'idNumber', e.target.value)}
                      label="NIK"
                      placeholder="16 Digit NIK"
                      error={errors.beneficialOwners?.[index]?.idNumber?.message}
                      helperText={errors.beneficialOwners?.[index]?.idNumber?.message}
                    />
                  </div>
                </div>

                <div className="row mb-12">
                  <div className="col-md-6">
                    <TextField
                      value={owner.birthPlace}
                      onChange={e => updateBeneficialOwner(index, 'birthPlace', e.target.value)}
                      label="Tempat Lahir"
                      error={errors.beneficialOwners?.[index]?.birthPlace?.message}
                      helperText={errors.beneficialOwners?.[index]?.birthPlace?.message}
                    />
                  </div>
                  <div className="col-md-6">
                    <DateField
                      label="Tanggal Lahir (DD/MM/YYYY)"
                      value={owner.birthDate}
                      onChange={v => updateBeneficialOwner(index, 'birthDate', v ? v : '')}
                      error={errors.beneficialOwners?.[index]?.birthDate?.message}
                      helperText={errors.beneficialOwners?.[index]?.birthDate?.message}
                      placeHolder="DD/MM/YYYY"
                    />
                  </div>
                </div>

                <div className="row mb-12">
                  <div className="col-md-6">
                    <TextField
                      value={owner.nationality}
                      onChange={e => updateBeneficialOwner(index, 'nationality', e.target.value)}
                      label="Kewarganegaraan"
                      error={errors.beneficialOwners?.[index]?.nationality?.message}
                      helperText={errors.beneficialOwners?.[index]?.nationality?.message}
                    />
                  </div>
                  <div className="col-md-6">
                    <TextField
                      value={owner.phoneNumber}
                      onChange={e => updateBeneficialOwner(index, 'phoneNumber', e.target.value)}
                      label="Nomor Telepon"
                      error={errors.beneficialOwners?.[index]?.phoneNumber?.message}
                      helperText={errors.beneficialOwners?.[index]?.phoneNumber?.message}
                    />
                  </div>
                </div>

                <TextField
                  value={owner.address}
                  onChange={e => updateBeneficialOwner(index, 'address', e.target.value)}
                  className="mb-12"
                  label="Alamat"
                  error={errors.beneficialOwners?.[index]?.address?.message}
                  helperText={errors.beneficialOwners?.[index]?.address?.message}
                />

                <div className="row mb-12">
                  <div className="col-md-6">
                    <TextField
                      value={owner.email}
                      onChange={e => updateBeneficialOwner(index, 'email', e.target.value)}
                      label="Email"
                      error={errors.beneficialOwners?.[index]?.email?.message}
                      helperText={errors.beneficialOwners?.[index]?.email?.message}
                    />
                  </div>
                  <div className="col-md-6">
                    <TextField
                      value={owner.relationship}
                      onChange={e => updateBeneficialOwner(index, 'relationship', e.target.value)}
                      label="Hubungan dengan Perusahaan"
                      error={errors.beneficialOwners?.[index]?.relationship?.message}
                      helperText={errors.beneficialOwners?.[index]?.relationship?.message}
                    />
                  </div>
                </div>
              </div>
            ))}

            {/* Sumber Dana Pemilik Manfaat */}
            <div className="mb-24">
              <p className="font-500 mb-12">Sumber Dana</p>
              <div className="mb-16">
                <Checkbox
                  name="beneficialOwnerFundSource.operatingProfit"
                  label="Laba Usaha / Operating Profit"
                  checked={beneficialOwnerFundSource.operatingProfit}
                  onChange={e => handleBeneficialOwnerFundSourceChange('operatingProfit', e.target.checked)}
                  color="primary"
                  className="mb-8"
                />
                <Checkbox
                  name="beneficialOwnerFundSource.investmentOutcomes"
                  label="Hasil Investasi / Investment outcomes"
                  checked={beneficialOwnerFundSource.investmentOutcomes}
                  onChange={e => handleBeneficialOwnerFundSourceChange('investmentOutcomes', e.target.checked)}
                  color="primary"
                  className="mb-8"
                />
                <Checkbox
                  name="beneficialOwnerFundSource.depositInterest"
                  label="Bunga Simpanan / Deposit Interest"
                  checked={beneficialOwnerFundSource.depositInterest}
                  onChange={e => handleBeneficialOwnerFundSourceChange('depositInterest', e.target.checked)}
                  color="primary"
                  className="mb-8"
                />
                <Checkbox
                  name="beneficialOwnerFundSource.loan"
                  label="Pinjaman / Loan"
                  checked={beneficialOwnerFundSource.loan}
                  onChange={e => handleBeneficialOwnerFundSourceChange('loan', e.target.checked)}
                  color="primary"
                  className="mb-8"
                />
                <div className="d-flex align-items-center">
                  <Checkbox
                    name="beneficialOwnerFundSource.others"
                    label="Lainnya, sebutkan / Others"
                    checked={beneficialOwnerFundSource.others}
                    onChange={e => handleBeneficialOwnerFundSourceChange('others', e.target.checked)}
                    color="primary"
                    className="mb-8"
                  />
                  {beneficialOwnerFundSource.others && (
                    <TextField
                      value={beneficialOwnerFundSource.othersText}
                      onChange={e => setBeneficialOwnerFundSource(prev => ({ ...prev, othersText: e.target.value }))}
                      placeholder="Sebutkan..."
                      className="ml-16"
                      style={{ flex: 1 }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* <RadioGroup
            row
            label="Volume Transaksi"
            value={transactionVolume}
            onChange={e => setValue('transactionVolume', e.target.value, { shouldValidate: true })}
            className="mb-24"
          >
            <Radio label="0-10/hari" value="0-10/hari" />
            <Radio label="11-100/hari" value="11-100/hari" />
            <Radio label="diatas 100/hari" value="diatas 100/hari" />
          </RadioGroup> */}
        </div>
        <div className={styles.fileInputWrapper}>
          <FileInput
            className="mb-24"
            ref={register}
            name="birthCertificate"
            label="Unggah Akta Pendirian Perusahaan (Max 2MB) (dengan format PDF)"
            error={errors.birthCertificate?.message}
            helperText={errors.birthCertificate?.message}
          />
          <FileInput
            className="mb-24"
            ref={register}
            name="sk"
            label="Unggah SK-Kemenkumham (Max 2MB) (dengan format PDF)"
            error={errors.sk?.message}
            helperText={errors.sk?.message}
          />
          <FileInput
            className="mb-24"
            ref={register}
            name="siupFile"
            label="Unggah NIB Perusahaan (Max 2MB) (dengan format PDF)"
            error={errors.siupFile?.message}
            helperText={errors.siupFile?.message}
          />
          <FileInput
            ref={register}
            name="npwpFile"
            label="Unggah NPWP Perusahaan (Max 2MB) (dengan format PDF)"
            error={errors.npwpFile?.message}
            helperText={errors.npwpFile?.message}
          />
        </div>
        <div className={styles.btnWrapper}>
          <MainButton
            className={styles.mainButton}
            type="submit"
            // disabled={!(
            //   birthCertificate && sk && siupFile && npwpFile && npwp && siup && formOfBusinessEntity && businessFields && transferFrequency && address && transactionValue && transactionVolume && isValid
            // )}
          >
            Lanjutkan
          </MainButton>
        </div>
      </form>
    </SimpleCard>
  )
}

export default CompanyForm
